import { Injectable } from '@angular/core';

export interface ReceiptRequiredModel {
  receipt_required?: string;
  matching_type?: string;
  is_receipt_required_locked?: boolean;
  is_receipt_required_backend_locked?: boolean;
}

@Injectable()
export class ReceiptRequiredService {
  matchingType3WayList: any[] = [
    { id: '1', name: '3 Way at Line Item' },
    { id: '2', name: '3 Way at Invoice Level' },
  ];

  matchingType2WayList: any[] = [
    { id: '3', name: '2 Way at Line Item' },
    { id: '4', name: '2 Way at Invoice Level' },
  ];

  parseReceiptRequiredFromApi(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '1';
    }
    const normalized = value.toString().toLowerCase();
    if (normalized === '0' || normalized === 'no' || normalized === 'false') {
      return '0';
    }
    return '1';
  }

  isPoReceiptRequiredFeatureEnabled(companySetup: any): boolean {
    return !!companySetup && companySetup.receipt_required_visible_in_po == '1';
  }

  getReceiptRequiredApiValue(uiValue: any, companySetup?: any): string {
    if (companySetup !== undefined && !this.isPoReceiptRequiredFeatureEnabled(companySetup)) {
      return '1';
    }
    return this.parseReceiptRequiredFromApi(uiValue) === '0' ? '0' : '1';
  }

  parseMatchingTypeFromApi(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '1';
    }
    const normalized = value.toString().trim().toLowerCase();
    const mapping: any = {
      '1': '1', '2': '2', '3': '3', '4': '4',
      '3_way_line_item': '1', '3_way_invoice': '2', '2_way_line_item': '3', '2_way_invoice': '4',
    };
    return mapping[normalized] || '1';
  }

  getMatchingTypeApiValue(value: any, companySetup?: any): number {
    if (companySetup !== undefined && !this.isPoReceiptRequiredFeatureEnabled(companySetup)) {
      return 1;
    }
    const parsed = this.parseMatchingTypeFromApi(value);
    return parsed ? parseInt(parsed, 10) : 1;
  }

  getMatchingTypeOptions(receiptRequiredUi: string): any[] {
    return receiptRequiredUi === '0' ? this.matchingType2WayList : this.matchingType3WayList;
  }

  getDefaultMatchingType(receiptRequiredUi: string): string {
    return receiptRequiredUi === '0' ? '3' : '1';
  }

  onReceiptRequiredChange(model: ReceiptRequiredModel, resetDefault: boolean = true): void {
    if (!model) {
      return;
    }
    if (model.receipt_required === '' || model.receipt_required === undefined || model.receipt_required === null) {
      model.receipt_required = '1';
    }
    const options = this.getMatchingTypeOptions(model.receipt_required);
    if (resetDefault) {
      model.matching_type = this.getDefaultMatchingType(model.receipt_required);
      return;
    }
    const validIds = options.map((option) => option.id);
    if (!validIds.includes(model.matching_type)) {
      model.matching_type = this.getDefaultMatchingType(model.receipt_required);
    }
  }

  getPoTypeDisplay(receiptRequiredUi: string): string {
    return this.parseReceiptRequiredFromApi(receiptRequiredUi) === '0' ? 'Receipt Not Required' : 'Receipt Required';
  }

  getMatchingTypeDisplay(value: any): string {
    const parsed = this.parseMatchingTypeFromApi(value);
    const allOptions = [...this.matchingType3WayList, ...this.matchingType2WayList];
    const match = allOptions.find((option) => option.id === parsed);
    return match ? match.name : '';
  }

  isPoReceiptRequiredLocked(po: any): boolean {
    if (!po) {
      return false;
    }
    if (po.is_receipt_required_locked == '1' || po.is_receipt_required_locked === true) {
      return true;
    }
    if (po.has_grn_srn == '1' || po.has_grn_srn === true) {
      return true;
    }
    if (po.has_invoice == '1' || po.has_invoice === true) {
      return true;
    }
    return false;
  }

  isPoReceiptRequiredExplicitlyLocked(po: any): boolean {
    return !!po && (po.is_receipt_required_locked == '1' || po.is_receipt_required_locked === true);
  }

  isApprovalScreen(): boolean {
    const screen = localStorage.getItem('po_screen');
    if (!screen) {
      return false;
    }
    try {
      return screen.indexOf('PO_APPROVAL') >= 0 || screen.indexOf('approval') >= 0;
    } catch (e) {
      return false;
    }
  }

  isReceiptRequiredReadOnly(isLocked: boolean, companySetup: any, isApprovalScreen?: boolean): boolean {
    if (isLocked) {
      return true;
    }
    const approvalMode = isApprovalScreen === undefined ? this.isApprovalScreen() : isApprovalScreen;
    if (approvalMode) {
      return !companySetup || companySetup.allow_po_approver_override_receipt_required != '1';
    }
    return !companySetup || companySetup.allow_po_creator_override_receipt_required != '1';
  }

  applyVendorDefaults(model: ReceiptRequiredModel, vendor: any, isLocked: boolean, companySetup?: any): void {
    if (!model) {
      return;
    }
    if (companySetup !== undefined && !this.isPoReceiptRequiredFeatureEnabled(companySetup)) {
      model.receipt_required = '1';
      model.matching_type = '1';
      return;
    }
    if (isLocked || !vendor) {
      return;
    }
    model.receipt_required = this.parseReceiptRequiredFromApi(vendor.receipt_required);
    model.matching_type = this.parseMatchingTypeFromApi(vendor.matching_type) || this.getDefaultMatchingType(model.receipt_required);
    this.onReceiptRequiredChange(model, false);
  }

  applyPoResponse(model: ReceiptRequiredModel, po: any, companySetup?: any): void {
    if (!model) {
      return;
    }
    if (companySetup !== undefined && !this.isPoReceiptRequiredFeatureEnabled(companySetup)) {
      model.receipt_required = '1';
      model.matching_type = '1';
      model.is_receipt_required_locked = false;
      model.is_receipt_required_backend_locked = false;
      return;
    }
    if (!po) {
      return;
    }
    model.receipt_required = this.parseReceiptRequiredFromApi(po.receipt_required);
    model.matching_type = this.parseMatchingTypeFromApi(po.matching_type) || this.getDefaultMatchingType(model.receipt_required);
    model.is_receipt_required_backend_locked = this.isPoReceiptRequiredExplicitlyLocked(po);
    model.is_receipt_required_locked = this.isPoReceiptRequiredLocked(po);
  }

  isReceiptNotRequired(value: any): boolean {
    return this.parseReceiptRequiredFromApi(value) === '0';
  }

  isTwoWayPo(po: any): boolean {
    if (!po) {
      return false;
    }
    // Backend flags (string/number/boolean)
    if (po.is_two_way == '1' || po.is_two_way === 1 || po.is_two_way === true) {
      return true;
    }
    if (po.is_two_way_po === true || po.is_two_way_po == '1' || po.is_two_way_po === 1) {
      return true;
    }
    if (po.is_two_way_invoice == '1' || po.is_two_way_invoice === 1 || po.is_two_way_invoice === true) {
      return true;
    }
    if (po.match_type_label && String(po.match_type_label).toLowerCase().indexOf('2-way') >= 0) {
      return true;
    }
    if (po.is_two_way_invoice_level == '1' || po.is_two_way_invoice_level === 1 || po.is_two_way_invoice_level === true) {
      return true;
    }
    if (this.isReceiptNotRequired(po.receipt_required)) {
      return true;
    }
    const matchingType = this.parseMatchingTypeFromApi(po.matching_type);
    return matchingType === '3' || matchingType === '4';
  }

  isInvoiceLevelMatchingType(matchingType: any, isTwoWay: boolean = false): boolean {
    const parsed = this.parseMatchingTypeFromApi(matchingType);
    return isTwoWay ? parsed === '4' : parsed === '2';
  }

  isLineItemMatchingType(matchingType: any, isTwoWay: boolean = false): boolean {
    const parsed = this.parseMatchingTypeFromApi(matchingType);
    return isTwoWay ? parsed === '3' : parsed === '1';
  }

  filterReceiptRequiredOnly(list: any[]): any[] {
    if (!Array.isArray(list)) {
      return [];
    }
    return list.filter((item) => {
      if (item.receipt_required === undefined || item.receipt_required === null || item.receipt_required === '') {
        return true;
      }
      return !this.isReceiptNotRequired(item.receipt_required);
    });
  }

  applyInvoicePoContext(model: any, po: any): void {
    if (!model || !po) {
      return;
    }
    model.receipt_required = this.parseReceiptRequiredFromApi(po.receipt_required);
    model.matching_type = this.parseMatchingTypeFromApi(po.matching_type) || this.getDefaultMatchingType(model.receipt_required);
    model.is_two_way_po = this.isTwoWayPo(po);
    model.is_two_way = model.is_two_way_po ? '1' : (po.is_two_way != null ? String(po.is_two_way) : '0');
    model.is_two_way_invoice = model.is_two_way_po ? '1' : (po.is_two_way_invoice != null ? String(po.is_two_way_invoice) : '0');
    model.match_type_label = po.match_type_label ? po.match_type_label : (model.is_two_way_po ? '2-Way' : model.match_type_label);
    model.po_type_display = this.getPoTypeDisplay(model.receipt_required);
    model.matching_type_display = this.getMatchingTypeDisplay(model.matching_type);
    // Keep original empty grn id separately; list id may be filled with po_id for uniqueness
    if (model.grn_id === undefined || model.grn_id === null) {
      model.grn_id = po.id || po.grn_id || '';
    }
    model.has_real_grn = !!(model.grn_id && String(model.grn_id).trim() !== '' && String(model.grn_id) !== String(po.po_id) && String(model.grn_id) !== String(po.po_no));
    // 2-way rows from ajax_getGrnWithoutInvoices often have empty grn id; keep a stable unique key
    if (!model.id && (po.po_id || po.cs_id || po.po_no)) {
      model.id = po.po_id || po.cs_id || po.po_no;
    }
    if (model.is_two_way_po) {
      model.match_3_way_at_invoice_level = false;
    }
  }

  markGrnSrnChecklistNotApplicable(checklistItems: any[]): void {
    if (!Array.isArray(checklistItems)) {
      return;
    }
    const grnItem = checklistItems.find((item) => item.key === 'grn_srn_available');
    const threeWayItem = checklistItems.find((item) => item.key === 'three_way_matching_done_item_level');
    if (grnItem) {
      grnItem.isChecked = '1';
      grnItem.status = 'N/A';
    }
    if (threeWayItem) {
      threeWayItem.isChecked = '1';
      threeWayItem.status = 'N/A';
    }
  }

  /**
   * Convert ajax_get_single_podetails response into the GRN-like payload
   * that Add Invoice (viewInvoiceData / SetInvoiceData) already expects.
   */
  mapPoDetailsToInvoicePayload(po: any, listRow: any = {}): any {
    if (!po) {
      return {};
    }
    const row = listRow || {};
    const items = Array.isArray(po.items)
      ? po.items
      : (Array.isArray(po.po_items) ? po.po_items : (Array.isArray(row.grn_items) ? row.grn_items : []));

    const formatLocName = (loc: any, fallback: string = ''): string => {
      if (!loc) {
        return fallback || '';
      }
      if (typeof loc === 'string') {
        return loc;
      }
      const nick = loc.location_nickname_9_digit || loc.nick_name || '';
      const code = loc.location_code_9_digit || loc.loc_9digit_code || '';
      if (nick && code) {
        return nick + ' (' + code + ')';
      }
      return nick || code || fallback || '';
    };

    const shipLocObj = po.ship_to_location_name;
    const billLocObj = po.bill_to_location_name;

    const grn_items = items.map((item: any) => {
      const qtySource = item.available_invoice_qty != null && item.available_invoice_qty !== ''
        ? item.available_invoice_qty
        : (item.balance_po_qty != null && item.balance_po_qty !== ''
          ? item.balance_po_qty
          : (item.product_qty != null && item.product_qty !== ''
            ? item.product_qty
            : (item.qty != null ? item.qty : (item.accepted_qty != null ? item.accepted_qty : 0))));
      const unitPrice = item.unit_price != null && item.unit_price !== ''
        ? item.unit_price
        : (item.product_unit_price != null ? item.product_unit_price : '0');
      return Object.assign({}, item, {
        grn_id: null,
        product_id: item.product_id ? item.product_id : '',
        status: item.status != null ? item.status : '1',
        qty: qtySource,
        accepted_qty: item.accepted_qty != null && item.accepted_qty !== '' ? item.accepted_qty : qtySource,
        received_qty: item.received_qty != null && item.received_qty !== '' ? item.received_qty : qtySource,
        rejected_qty: item.rejected_qty != null && item.rejected_qty !== '' ? item.rejected_qty : '0',
        damaged_qty: item.damaged_qty != null && item.damaged_qty !== '' ? item.damaged_qty : '0',
        productcode: item.productcode ? item.productcode : (item.product_code ? item.product_code : ''),
        product_name: item.product_name ? item.product_name : '',
        product_type: item.product_type ? item.product_type : '',
        unit_type_name: item.unit_type_name ? item.unit_type_name : '',
        unit_price: unitPrice,
        product_unit_price: item.product_unit_price != null ? item.product_unit_price : unitPrice,
        total_required_qty: item.total_required_qty != null && item.total_required_qty !== ''
          ? item.total_required_qty
          : (item.product_qty != null ? item.product_qty : qtySource),
        available_invoice_qty: item.available_invoice_qty != null && item.available_invoice_qty !== ''
          ? item.available_invoice_qty
          : qtySource,
        balance_po_qty: item.balance_po_qty != null ? item.balance_po_qty : qtySource,
        po_item_id: item.po_item_id ? item.po_item_id : (item.id ? item.id : ''),
        po_id: item.po_id ? item.po_id : (po.id ? po.id : (po.po_id ? po.po_id : '')),
        product_cgst: item.product_cgst != null ? item.product_cgst : (item.cgst != null ? item.cgst : (item.hsn_cgst || 0)),
        product_sgst: item.product_sgst != null ? item.product_sgst : (item.sgst != null ? item.sgst : (item.hsn_sgst || 0)),
        product_igst: item.product_igst != null ? item.product_igst : (item.igst != null ? item.igst : (item.hsn_igst || 0)),
        product_ugst: item.product_ugst != null ? item.product_ugst : (item.ugst != null ? item.ugst : (item.hsn_ugst || 0)),
        product_cess: item.product_cess != null ? item.product_cess : (item.cess != null ? item.cess : (item.hsn_cess || 0)),
        product_other_tax: item.product_other_tax != null ? item.product_other_tax : (item.other_tax != null ? item.other_tax : (item.hsn_other_tax || 0)),
        is_closed: item.is_closed != null ? item.is_closed : '0',
        is_temp_prod: item.is_temp_prod != null ? item.is_temp_prod : '0',
        is_custom_ship_to_location: item.is_custom_ship_to_location != null ? item.is_custom_ship_to_location : (po.is_custom_ship_to_location || '0'),
        custom_ship_to_location: item.custom_ship_to_location != null ? item.custom_ship_to_location : (po.custom_ship_to_location || ''),
        project_id: item.project_id != null ? item.project_id : (po.project_id || ''),
        task_id: item.task_id != null ? item.task_id : (po.task_id || ''),
      });
    });

    const payload: any = Object.assign({}, row, po, {
      id: po.id || row.id || po.po_id || row.po_id || '',
      cs_id: row.cs_id || po.po_id || po.po_no || row.po_id || '',
      grn_id: '',
      grn_date: row.grn_date || po.po_date || '',
      // Numeric PK from ajax_get_single_podetails (e.g. "1413") — used as po_id on invoice save
      po_pk_id: po.id != null && po.id !== '' ? String(po.id) : (row.po_pk_id ? String(row.po_pk_id) : ''),
      // Keep string PO number for UI / other APIs
      po_id: po.po_id || row.po_id || '',
      po_no: po.po_no || po.po_id || row.po_no || row.po_id || '',
      po_group_id: po.po_group_id || row.po_group_id || '',
      vendor_id: po.vendor_id || po.vendor || row.vendor_id || '',
      vendor_location_id: po.vendor_location_id || row.vendor_location_id || '',
      ship_to_location: po.ship_to_location || row.ship_to_location || row.ship_location_id || '',
      bill_to_location: po.bill_to_location || row.bill_to_location || row.bill_location_id || '',
      ship_to_location_name: formatLocName(shipLocObj, row.ship_to_location_name || po.ship_to_location_name || ''),
      bill_to_location_name: formatLocName(billLocObj, row.bill_to_location_name || po.bill_to_location_name || ''),
      ship_to_location_nickname_9_digit: (shipLocObj && shipLocObj.location_nickname_9_digit)
        ? shipLocObj.location_nickname_9_digit
        : (po.ship_to_location_nickname_9_digit || row.ship_to_location_name || ''),
      ship_to_location_code_9_digit: (shipLocObj && shipLocObj.location_code_9_digit)
        ? shipLocObj.location_code_9_digit
        : (po.ship_to_location_code_9_digit || ''),
      bill_to_location_nickname_9_digit: (billLocObj && billLocObj.location_nickname_9_digit)
        ? billLocObj.location_nickname_9_digit
        : (po.bill_to_location_nickname_9_digit || row.bill_to_location_name || ''),
      bill_to_location_code_9_digit: (billLocObj && billLocObj.location_code_9_digit)
        ? billLocObj.location_code_9_digit
        : (po.bill_to_location_code_9_digit || ''),
      ship_to_location_gst_no: po.ship_to_location_gst_no || (shipLocObj && shipLocObj.gst_no) || row.ship_to_location_gst_no || '',
      bill_to_location_gst_no: po.bill_to_location_gst_no || (billLocObj && billLocObj.gst_no) || row.bill_to_location_gst_no || '',
      vendor_address: po.vendor_address || row.vendor_address || '',
      vendor_first_name: po.vendor_first_name || row.vendor_first_name || '',
      vendor_last_name: po.vendor_last_name || row.vendor_last_name || '',
      vendor_code: po.vendor_code || row.vendor_code || '',
      irn_cs_id: row.irn_cs_id || '',
      irn_date: row.irn_date || '',
      grn_items: grn_items,
      is_two_way: '1',
      is_two_way_invoice: '1',
      receipt_required: this.parseReceiptRequiredFromApi(
        po.receipt_required != null && po.receipt_required !== '' ? po.receipt_required : (row.receipt_required != null ? row.receipt_required : '0')
      ),
      matching_type: this.parseMatchingTypeFromApi(
        po.matching_type != null && po.matching_type !== '' ? po.matching_type : row.matching_type
      ),
    });

    this.applyInvoicePoContext(payload, payload);
    return payload;
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src2094230074/src/src/app/shared/services/receipt-required.service.ts